import { useMutation } from '@apollo/client';
import type {
  InsertQuestionnaireTemplateMutation,
  InsertQuestionnaireTemplateMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertQuestionnaireTemplateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertQuestionnaireTemplateTRPC } from './useInsertQuestionnaireTemplateTRPC';

export const useInsertQuestionnaireTemplate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [insertQuestionnaireTemplateGraphQL, graphqlState] = useMutation(
    InsertQuestionnaireTemplateDocument,
    {
      update: (cache) => {
        evictField(cache, 'questionnaire_template');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertQuestionnaireTemplateTRPC();

  const insertQuestionnaireTemplate = async (input: {
    variables: InsertQuestionnaireTemplateMutationVariables;
  }): Promise<InsertQuestionnaireTemplateMutation> => {
    if (trpcEnabled) {
      const object = input.variables.object;

      return trpcMutation.insertQuestionnaireTemplate({
        Title: object?.Title ?? '',
        Description: object?.Description,
        CustomAttributeData: object?.CustomAttributeData,
        OwnerUserIds: object?.OwnerUserIds ?? [],
        OwnerGroupIds: object?.OwnerGroupIds ?? [],
        ContributorUserIds: object?.ContributorUserIds ?? [],
        ContributorGroupIds: object?.ContributorGroupIds ?? [],
        ViewerUserIds: object?.ViewerUserIds,
        ViewerGroupIds: object?.ViewerGroupIds,
        TagTypeIds: object?.TagTypeIds ?? [],
        DepartmentTypeIds: object?.DepartmentTypeIds ?? [],
      });
    }

    const result = await insertQuestionnaireTemplateGraphQL({
      variables: input.variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert questionnaire template');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      insertQuestionnaireTemplate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertQuestionnaireTemplate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
